import type { Store } from 'pinia';
import type {
  Component,
  ComputedRef,
  Ref,
  VNode,
  VNodeArrayChildren,
} from 'vue';

import type { ClientResource, Role } from '@/features/Clients';
import type { SpartenConfigGeneral } from '@/features/Sparten/types';
import type { LabelLink } from '@/utils/linkify/splitLabelUrls';

import { ValidationError } from 'yup';

// FormConfig Types
export interface RenderingContext<T extends ClientResource = ClientResource> {
  index?: number;
  client?: T;
}

export type ComputedOrBoolFunction<T extends ClientResource = ClientResource> =
  | ComputedRef<boolean>
  | ((context: RenderingContext<T>) => boolean);

export interface RenderItem {
  component: string | Component;
  inputAttrs?: Record<string, unknown>;
  isDisabled?: ComputedOrBoolFunction<any>;
  renderWhen?: ComputedOrBoolFunction<any>;
}

export interface HandlerFuncParams {
  index?: number;
}

export type HandlerFunction = (options: HandlerFuncParams) => void;

export interface SimpleFieldConfig extends RenderItem {
  slots?: Record<string, () => string | VNode | VNodeArrayChildren>;
  handlers?: Record<string, HandlerFunction>;
}

export enum INFO_ICON_TYPE {
  POPOVER = 'popover',
  MODAL = 'modal',
}

export interface InputFieldConfig extends RenderItem {
  path: string | ComputedRef<string>;
  label: string | ComputedRef<string>;
  /** Links, die neben dem Label angezeigt werden (z. B. aus Gesellschaftsfragen) */
  labelLinks?: LabelLink[];
  helpText?: string | ComputedRef<string>;
  isRequired?: boolean;
  isRequiredWhen?: ComputedRef<boolean>;
  formGroupClass?: string[];
  skipLiveValidation?: boolean;
  infoIcon?: {
    type: INFO_ICON_TYPE;
    title?: string;
    content: string | ComputedRef<string>;
  };

  skipLiveValidationWhenEqual?: unknown[];
  revalidate?: string[];
  revalidateClient?: string[];
  inlineGroup?: boolean;
  hideErrorMessage?: boolean;
  inlineGroupReversed?: boolean;
  disableVersicherungsangabenDirtyCheck?: boolean;
  disableVersicherungsangabenDirtyIndicator?: boolean;
}

export interface ClientFieldConfig extends InputFieldConfig {
  clientField: true;
}

export interface ContractFieldConfig extends InputFieldConfig {
  contractField: true;
}

export type NestedFieldConfigGroup = (
  | SimpleFieldConfig
  | InputFieldConfig
  | ClientFieldConfig
  | ContractFieldConfig
)[]; // for nested inputs (2 components in 1 row)

export type FormFieldConfig =
  | SimpleFieldConfig
  | InputFieldConfig
  | ClientFieldConfig
  | ContractFieldConfig
  | NestedFieldConfigGroup;

export interface ArrayRenderGroup extends Omit<RenderItem, 'component'> {
  component?: string | Component;
  renderCount: ComputedRef<number> | number[];
  childWrapperClasses?: string;
  fieldConfigs: (
    | InputFieldConfig
    | ClientFieldConfig
    | ContractFieldConfig
    | SimpleFieldConfig
  )[];
}

export interface RenderGroup extends RenderItem {
  fieldConfigs: (FormFieldConfig | ArrayRenderGroup | RenderGroup)[];
  childWrapperClasses?: string;
}

export type FieldConfig =
  | RenderGroup
  | ArrayRenderGroup
  | FormFieldConfig
  | SimpleFieldConfig;

export interface FormConfig {
  formName: string;
  fieldConfigs: FieldConfig[];
  gridClasses?: string;
}

// Util internal types
export interface FieldPropsDefinition {
  input?: Store;
  contract?: Store;
  client?: ClientResource;
}

export interface FieldEmitDefinition {
  (arg: string, args?: unknown): void;
}

export interface RenderOptions {
  highlightProfileFields: Ref<boolean>;
  spartenConfig: SpartenConfigGeneral;
  role: Role | undefined;
  props: FieldPropsDefinition;
  emit: FieldEmitDefinition;
  clientValidateFunc: ((path: string) => void) | undefined;
  validateFunc: ((path: string) => void) | undefined;
}

// Type Guards
export const isNestedFieldConfigGroup = (
  confObj: FormFieldConfig | RenderGroup | ArrayRenderGroup,
): confObj is NestedFieldConfigGroup => Array.isArray(confObj);

export const isRenderGroup = (
  confObj: FormFieldConfig | RenderGroup | ArrayRenderGroup,
): confObj is RenderGroup =>
  Object.hasOwn(confObj, 'component') &&
  Object.hasOwn(confObj, 'fieldConfigs') &&
  !Object.hasOwn(confObj, 'renderCount');

export const isArrayRenderGroup = (
  confObj: FormFieldConfig | RenderGroup | ArrayRenderGroup,
): confObj is ArrayRenderGroup => Object.hasOwn(confObj, 'renderCount');

export const isClientFieldConfig = (
  fieldConfig: FormFieldConfig,
): fieldConfig is ClientFieldConfig =>
  (fieldConfig as ClientFieldConfig).clientField === true;

export const isContractFieldConfig = (
  fieldConfig: FormFieldConfig,
): fieldConfig is ContractFieldConfig =>
  (fieldConfig as ContractFieldConfig).contractField === true;

export const isSimpleFieldConfig = (
  fieldConfig: SimpleFieldConfig,
): fieldConfig is SimpleFieldConfig =>
  !Object.hasOwn(fieldConfig, 'path') &&
  !Object.hasOwn(fieldConfig, 'renderCount') &&
  !Object.hasOwn(fieldConfig, 'fieldConfigs');

export class SilentValidationError extends ValidationError {}
